<template>
  <main class="chief-page">
    <div class="chief-page__body">
      <h1 class="chief-page__title">Модуль главного врача</h1>
      <template v-if="isLoaded">
        <article class="visit-chief-physician-status">
          <span
            class="visit-chief-physician-status__title"
            v-bind:class="review.id"
          >{{ review.title }}</span>
          <p v-if="review.note">{{ review.note }}</p>
          <strong v-if="explanationErrors.needsShowDescription">
            {{ explanationErrors.description }}
          </strong>
          <div
            class="visit-chief-physician-status__table-wrapper"
            v-else-if="explanationErrors.needsShow"
          >
            <table class="visit-chief-physician-status__table-detailing">
              <thead>
                <tr>
                  <th
                    v-bind:key="`explanation-errors:cells:${cellIndex}`"
                    v-for="(title, cellIndex) in explanationErrors.columns"
                  >{{ title }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-bind:key="`explanation-errors:rows:${rowIndex}`"
                  v-for="(cells, rowIndex) in explanationErrors.rows"
                >
                  <td
                    v-bind:key="`explanation-errors:rows:${rowIndex}:cells:${cellIndex}`"
                    v-for="(title, cellIndex) in cells"
                  >{{ title }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>
        <div
          class="chief-page__buttons"
          v-if="hasChangeReviewStatus"
        >
          <button
            class="chief-page__button-submit"
            type="button"
            v-bind:disabled="isAwaitRequest"
            v-on:click="shownNoteModalDialog = true">
            Отправить на доработку
          </button>
          <router-link
            v-bind:to="visitRouterLinkTo"
            v-if="isEditVisitAllowed"
            class="chief-page__button-edit"
          >Редактировать</router-link>
        </div>
        <patient-section-control class="patient-visits__sections" multiple>
          <section class="patient-visits__visit">
            <div class="patient-visits__visit__row">
              <div class="patient-visits__visit__group">
                <h3 class="patient-visits__visit__title">{{ visitInfo.title }}</h3>
                <time class="patient-visits__visit__datetime">
                  {{ visitInfo.date }}
                </time>
                <div class="patient-visits__visit__datetime">
                  <time v-bind:datetime="visitInfo.time">{{ visitInfo.time }}</time>
                </div>
              </div>
            </div>
            <dl class="patient-visits__visit__doctor">
              <dt>Пациент:</dt>
              <dd class="patient-visits__visit__doctor__name">{{ visitInfo.patientName }}</dd>
            </dl>
            <dl class="patient-visits__visit__doctor">
              <dt>Врач:</dt>
              <dd class="patient-visits__visit__doctor__name">{{ visitInfo.employeeName }}</dd>
            </dl>
          </section>
          <patient-section :label="''" class="patient-visits__dental-formula">
            <template slot="header">
              <div class="patient-visits__visit__bite">
                <p>Зубная формула</p>
                <div class="patient-visits__visit__bite__container">
                  <div class="patient-visits__visit__bite__title">Молочный прикус</div>
                  <input
                    type="checkbox"
                    class="patient-visits__visit__checkbox"
                    v-on:click.stop
                    v-model="isMilkBite"
                    id="MilkBite"
                  />
                  <label for="MilkBite" v-on:click.stop />
                </div>
              </div>
            </template>
            Выберите зуб или несколько зубов(для выбора нескольких зубов зажмите ctrl)
            <dental-formula
              only-view
              v-bind:diagnoses="dentalFormulaDiagnoses"
              v-bind:is-milk-bite="isMilkBite"
              v-bind:teeth="dentalFormulaTeeth"
            />
          </patient-section>
          <patient-section
            label="Протоколы приема"
            v-bind:description="diariesDescription"
            v-bind:disabled="hasNoDiaries"
          >
            <patient-section
              v-for="(diary, diaryIndex) in diaries"
              v-bind:key="`diary-${diaryIndex}`"
              v-bind:label="`Протокол ${diaryIndex + 1}`"
              v-bind:description="diary.description"
            >
              <div class="patient-visits__diary">
                <textarea-length-limitation
                  disabled
                  rows="4"
                  v-bind:key="`diary-${diaryIndex}:field-${fieldIndex}`"
                  v-bind:max-length="$options.textFieldMaxLength"
                  v-bind:placeholder="title"
                  v-bind:showCounter="false"
                  v-bind:title="title"
                  v-bind:value="value"
                  v-for="({ title, value }, fieldIndex) in diary.fields" />
                <div class="patient-visits__diary__block">
                  МКБ-10 <span class="patient-visits__diary__disease">{{ diary.icdName }}</span>
                </div>
                <input-checkbox
                  class="visit-service-table__cell"
                  disabled
                  title="Санирован"
                  v-model="visitInfo.sanitized" />
              </div>
            </patient-section>
          </patient-section>
          <section class="patient-visits__services">
            <div class="patient-visits__services__row">
              <div class="patient-visits__services__title">Услуги</div>
            </div>
            <visit-service-table
              only-view
              v-bind:treatments-plan-template-categories="treatmentsPlanTemplateCategories"
              v-bind:initial-discount-type="discountType"
              v-bind:is-milk-bite-default="isMilkBite"
              v-bind:rows="serviceList.rows"
            />
          </section>
        </patient-section-control>
        <div
          class="chief-page__buttons"
          v-if="hasChangeReviewStatus"
        >
          <button
            class="chief-page__button-submit"
            type="button"
            v-bind:disabled="isAwaitRequest"
            v-on:click="shownNoteModalDialog = true">
            Отправить на доработку
          </button>
          <router-link
            v-if="isEditVisitAllowed"
            class="chief-page__button-edit"
            v-bind:to="visitRouterLinkTo"
          >Редактировать</router-link>
        </div>
      </template>
    </div>
    <modal-dialog
      persistent
      v-model="shownNoteModalDialog"
      title="Отправить на доработку"
    >
      <form v-on:submit.prevent="onSendVisitForRevision" class="chief-page__modal">
        <textarea-length-limitation
          rows="7"
          title="Комментарий"
          v-bind:max-length="1000"
          v-model="note"
        />
        <div class="chief-page__modal__buttons">
          <button
            class="chief-page__button-cancel"
            type="button"
            v-on:click="shownNoteModalDialog = false">Отмена</button>
          <button
            class="chief-page__button-submit"
            type="submit">Отправить</button>
        </div>
      </form>
    </modal-dialog>
  </main>
</template>

<script>
import notifierMixin from '@/mixins/notifierMixin';
import { Maybe } from 'monet';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import DentalFormula from '@/components/DentalFormula.vue';
import PatientSection, { PatientSectionControl } from '@/components/PatientSection.vue';
import VisitServiceTable from '@/components/VisitServiceTable.vue';
import InputCheckbox from '@/components/InputCheckbox.vue';
import { DISCOUNT_MIXED } from '@/components/SwitchDiscountType.vue';

/**
 * @description Примесь зубной формулы.
 * @type {Vue}
 */
const dentalFormulaMixin = {
  computed: {
    /**
     * @description Зубы зубной формулы.
     * @type {Object.<number,Tooth>}
     */
    dentalFormulaTeeth() {
      return this.visit.fold({})(({ dentalFormula }) => dentalFormula);
    },
    /**
     * @description Зубы.
     * @type {Object}
     */
    teeth() {
      const partMapper = (part) => ({
        id: part.id,
        diagnoses: part.diagnoses.map((diagnosis) => diagnosis.id),
      });
      return Object.keys(this.dentalFormulaTeeth).map((number) => {
        const tooth = this.dentalFormulaTeeth[number];
        const parts = tooth.parts.map(partMapper);
        return { number, parts };
      }, {});
    },
  },
  data() {
    return {
      /**
       * @description Диагнозы зубной формулы.
       * @type {Object.<string,Diagnosis>}
       */
      dentalFormulaDiagnoses: {},
      /**
       * @description Молочный прикус.
       * @type {boolean}
       */
      isMilkBite: false,
    };
  },
  methods: {
    /**
     * @description Получить диагнозы зубной формулы.
     * @returns {void}
     */
    fetchDentalFormulaDiagnosis() {
      const onResponse = (diagnoses) => { this.dentalFormulaDiagnoses = diagnoses; };
      this
        .$patientGateway
        .getDentalFormulaDiagnoses()
        .then((result) => result.forEach(onResponse));
    },
  },
};

/**
 * @description Страница модуля главного врача.
 * @type {Vue}
 */
export default {
  components: {
    TextareaLengthLimitation,
    ModalDialog,
    DentalFormula,
    PatientSection,
    PatientSectionControl,
    VisitServiceTable,
    InputCheckbox,
  },
  computed: {
    /**
     * @description Идентификатор визита.
     * @type {number}
     */
    visitId() {
      return +this.$route.params.id;
    },
    /**
     * @description Визит загружен.
     * @type {boolean}
     */
    isLoaded() {
      return this.visit.isSome();
    },
    /**
     * @description Информация о визите.
     * @type {VisitInfo}
     */
    visitInfo() {
      return this.visit.fold({})(({ visitInfo }) => visitInfo);
    },
    /**
     * @description Дневник приема.
     * @type {Diary[]}
     */
    diaries() {
      return this.visit.fold([])(({ diaries }) => diaries);
    },
    /**
     * @description Пояснение к протоколам.
     * @type {string}
     */
    diariesDescription() {
      if (this.hasNoDiaries) {
        return 'Нет заполненных протоколов приема';
      }
      return '';
    },
    /**
     * @description Не имеет заполненных протоколов.
     * @type {boolean}
     */
    hasNoDiaries() {
      return this.diaries.length === 0;
    },
    /**
     * @description Состояние проверки главным врачом.
     * @type {Object}
     */
    review() {
      return this.visit.fold({})(({ review }) => ({
        id: review.getId(),
        note: review.getNote(),
        title: review.getTitle(),
      }));
    },
    discountType() {
      return this.visit.fold(DISCOUNT_MIXED)(({ discountType }) => discountType);
    },
    explanationErrors() {
      return this.visit.fold({ needsShow: false, needsShowDescription: false })(
        ({
          explanationErrorColumns: columns,
          explanationErrorDescription: description,
          explanationErrorRows: rows,
        }) => ({
          columns,
          description,
          needsShowDescription: description !== '',
          needsShow: rows.length !== 0,
          rows,
        }),
      );
    },
    /**
     * @description Список услуг.
     * @type {ServiceTable}
     */
    serviceList() {
      return this.visit.fold({})(({ services }) => services);
    },
    /**
     * @description Изменять статус проверки визита.
     * @returns {boolean}
     */
    hasChangeReviewStatus() {
      return this.visit.fold(false)(({ actions }) => actions.includes('change-status'));
    },
    /**
     * @description Имеет право на редактирование Визита.
     */
    isEditVisitAllowed() {
      return this.visit.fold(false)(({ actions }) => actions.includes('edit'));
    },
    /**
     * @description Параметры маршрута для перехода в Визит.
     */
    visitRouterLinkTo() {
      return this.visit.fold({})(({ visitInfo }) => ({
        name: 'ClinicPanelPatientVisit',
        params: {
          id: visitInfo.patientId,
          visitId: visitInfo.id,
        },
      }));
    },
  },
  created() {
    this.fetchTreatmentPlatTemplateCategories();
    this.fetchDentalFormulaDiagnosis();
    this.fetchVisit();
  },
  data() {
    return {
      /**
       * @description Категории шаблонов планов лечения.
       * @type {Array<PlanCategory>}
       */
      treatmentsPlanTemplateCategories: [],
      isAwaitRequest: false,
      note: '',
      shownNoteModalDialog: false,
      visit: Maybe.none(),
    };
  },
  methods: {
    /**
     * @description Затянуть категории шаблонов планов лечения.
     * @returns {void}
     */
    async fetchTreatmentPlatTemplateCategories() {
      this.$planGateway
        .getCategories()
        .then((result) => {
          result.forEach((categories) => {
            this.treatmentsPlanTemplateCategories = categories;
            this.isAwaitTemplates = false;
          });
        })
        .finally(() => {
          this.isAwaitTemplates = false;
        });
    },
    /**
     * @description Получить визит.
     * @returns {void}
     */
    fetchVisit() {
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      const onSuccess = (visit) => {
        this.visit = Maybe.some(visit);
        this.inputErrors = {};
      };
      this.$chiefPhysicianGateway.viewById(this.visitId).then(
        (result) => result.fold(onFail, onSuccess),
      );
    },
    /**
     * @description Отправить визит на доработку.
     * @returns {void}
     */
    onSendVisitForRevision() {
      const onSuccess = (response) => {
        this.shownNoteModalDialog = false;
        this.notifySuccess(response.message);
        this.openVisitList();
      };
      const onFail = (error) => {
        this.notifyError(error.getTitle());
      };
      this.isAwaitRequest = true;
      this
        .$chiefPhysicianGateway
        .sendVisitForRevision(this.visitId, this.note)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
    /**
     * @description Открыть список визитов.
     * @returns {void}
     */
    openVisitList() {
      this.$router.push({ name: 'ClinicPanelChiefPhysicianVisitList' });
    },
  },
  mixins: [dentalFormulaMixin, notifierMixin],
  name: 'ClinicPanelChiefPhysicianVisit',
  textFieldMaxLength: 5000,
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

.chief-page {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  color: #323c47;
  overflow-y: auto;
  &__body {
    padding: 38px 20px 48px 23px;
    width: 100%;
    overflow-y: auto;
  }
  &__title {
    margin-bottom: 30px;
    color: var(--reading-color);
  }
  &__buttons {
    margin: 15px 0;
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    gap: 10px;
  }
  &__button {
    &-cancel {
      @include buttons.btn-transparent(100%, 48px, 18px);
      max-width: 256px;
    }
    &-submit {
      @include buttons.btn-blue(100%, 48px, 18px);
      max-width: 256px;
    }
    &-edit {
      @include buttons.btn-blue(100%, 48px, 18px);
      color: #FFFFFF!important;
      text-decoration: none !important;
      max-width: 256px;
    }
  }
  &__modal {
    display: flex;
    flex-direction: column;
    gap: 10px;
    &__buttons {
      display: grid;
      grid-template-columns: repeat(2, 256px);
      column-gap: 10px;
    }
  }
}

.patient-visits {
  display: flex;
  flex-direction: column;
  padding-top: 15px;
  &__section {
    margin: 25px 0 0 0;
    position: relative;
  }
  &__slide {
    text-align: left;
    background: #eaeff3;
    border-radius: 4px;
    padding: 14px 19px;
    font-weight: 500;
    font-size: 17px;
    line-height: 20px;
    color: #192a3e;
    margin: 0 3px;
    cursor: pointer;
    border: 2px solid transparent;
    &.selected {
      border: 2px solid #0489e0;
      color: #0489e0;
    }
    &:hover {
      border: 2px solid #0489e0;
    }
    &__row {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      justify-content: space-between;
      gap: 10px;
      margin: 0 0 5px 0;
    }
    &__name {
      color: #90a0b7;
      font-weight: 400;
      font-size: 14px;
      line-height: 17px;
    }
    &__icon-delete {
      align-self: center;
      justify-self: center;
      background-image: url("@/assets/img/exit-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      width: 14px;
      height: 14px;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/exit-blue.svg");
      }
    }
  }
  &__dental-formula {
    .patient-section__body {
      margin: 25px 0 0 -24px;
    }
  }
  &__empty {
    text-align: left;
    font-size: 18px;
    cursor: pointer;
    b {
      color: var(--action-color);
    }
  }
  &__visits {
    display: flex;
    flex-direction: column;
    gap: 6px;
    &__item {
      background: #eaeff3;
      border-radius: 4px;
      transition: border var(--animate-duration);
      padding: 13px 16px;
      border: 2px solid #eaeff3;
      &:hover {
        border: 2px solid #0489e0;
      }
      &__name {
        color: #90a0b7;
        font-weight: 400;
        font-size: 14px;
        line-height: 17px;
      }
      time {
        color: #192a3e;
        font-size: 17px;
        font-weight: 500;
        line-height: 20px;
      }
    }
  }
  &__sections {
    z-index: 0;
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 13px;
  }
  &__visit {
    background-color: #f2f5f8 !important;
    padding: 26px 26px 34px;
    width: 100%;
    &__row {
      display: grid;
      align-items: center;
      column-gap: 10px;
      grid-template-columns: 1fr 174px 174px;
    }
    &__group {
      align-items: center;
      display: grid;
      column-gap: 15px;
      grid-template-columns: repeat(3, min-content) 1fr;
      white-space: nowrap;
      width: 100%;
    }
    &__datetime {
      color: var(--action-color);
      font-size: 18px;
      font-weight: 700;
      line-height: 22px;
      border-bottom: 1px solid #abd5f1;
      cursor: default;
      width: fit-content;
    }
    &__doctor {
      display: flex;
      flex-direction: row;
      grid-gap: 15px;
      font-weight: 400;
      color: #192a3e;
      font-size: 16px;
      line-height: 19px;
      margin: 20px 0 0 0;
      align-items: center;
      width: fit-content();
      &__name {
        font-weight: 600;
      }
    }
    &__bite {
      display: flex;
      flex-direction: row;
      align-items: center;
      p {
        margin: 0;
      }
      &__container {
        margin-left: 54px;
        display: flex;
        flex-direction: row;
        align-items: center;
        background-color: #ffffff;
        border-radius: 40px;
        padding: 7px 6px 7px 14px;
        gap: 7px;
        .v-input--selection-controls__input:hover
        .v-input--selection-controls__ripple:before {
          content: none;
        }
        .theme--light.v-input--switch .v-input--switch__track {
          background: #eaeff3 !important;
          border: 1px solid #c2cfe0;
          border-radius: 15px;
        }
        .theme--light.v-input--switch .v-input--switch__thumb {
          color: #90a0b7;
        }
        .v-input--selection-controls {
          margin: 0 !important;
          padding: 5px !important;
        }
      }
      &__title {
        font-weight: 400;
        font-size: 14px;
        line-height: 17px;
        color: #192a3e;
      }
    }
    &__checkbox {
      position: absolute;
      z-index: -1;
      opacity: 0;
      &:checked + label:before {
        background: rgba(4, 137, 224, 0.18);
        border: 1px solid #0489e0;
      }
      &:checked + label:after {
        left: 16px;
        background: #0489e0;
      }
    }
    &__checkbox + label {
      position: relative;
      padding: 0 0 16px 40px;
      cursor: pointer;
    }
    &__checkbox + label:before {
      content: "";
      position: absolute;
      top: -4px;
      left: 0;
      width: 38px;
      height: 24px;
      border-radius: 15px;
      background: #eaeff3;
      border: 1px solid #c2cfe0;
      transition: 0.2s;
    }
    &__checkbox + label:after {
      content: "";
      position: absolute;
      top: -2px;
      left: 2px;
      width: 20px;
      height: 20px;
      border-radius: 10px;
      background: #90a0b7;
      transition: 0.2s;
    }
  }
  &__diary {
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-width: 737px;
    &__block {
      display: flex;
      flex-direction: column;
      gap: 10px;
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      color: #192a3e;
      margin: -15px 0 0 0;
    }
  }
  &__diary-template {
    max-width: 737px;
  }
  &__diary-template + &__diary {
    margin-top: 2rem;
  }
  &__services {
    margin-top: 63px;
    margin-bottom: 82px;
    width: 100%;
    &__row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 69px;
    }
    &__title {
      font-size: 24px;
      font-weight: 600;
      line-height: 29px;
    }
  }
}

.teeth-formula {
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 19px 10px;
  border-radius: 8px;
  overflow: hidden;
  gap: 17px;
  &__row {
    display: grid;
    grid-template-columns: repeat(16, 68px);
    align-items: center;
    text-align: center;
  }
}

.title {
  position: absolute;
  z-index: 2;
}

.print-visit-form {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  &__document {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 10px;
    &__count {
      input {
        border-bottom: 1px solid #C2CFE0;
        box-sizing: border-box;
        appearance: textfield;
        text-align: center;
        height: 30px;
        width: 30px;
        margin: 0 7px;
        color: #707683;
        font-size: 14px;
        &:focus {
          color: #192a3e;
          background-color: #fff;
          border-bottom: 1px solid #0489e0 !important;
          outline: 0;
        }
        &:disabled {
          background-color: #fff;
        }
      }
    }
  }
  &__button {
    @include buttons.btn-blue(100%, 32px, 12px);
    max-width: 150px;
    align-self: flex-end;
  }
}

.mdi-menu-down::before {
  content: "";
  background-image: url("@/assets/img/checked/checked-grey.svg");
  background-size: contain;
  background-position: center;
  width: 12px;
  height: 10px;
  cursor: pointer;
}

.visit-chief-physician-status {
  padding: 10px;
  margin: 15px 0 15px 0;
  border: 1px solid #c2cfe0;
  &__title {
    display: flex;
    align-items: center;
    &.new,
    &.register-error {
      gap: 14px;
      &:before {
        width: 6px;
        height: 6px;
        background-color: #abb1a3;
        border-radius: 50%;
        content: "";
      }
    }
    &.to-verify {
      gap: 12px;
      &:before {
        width: 10px;
        height: 10px;
        border: 2px solid #faa525;
        border-radius: 50%;
        content: "";
      }
    }
    &.rejected {
      color: var(--warning-color);
      gap: 7px;
      &:before {
        background-image: url("@/assets/img/exit-red.svg");
        background-repeat: no-repeat;
        background-size: contain;
        content: "";
        height: 20px;
        width: 20px;
      }
    }
    &.verified {
      gap: 7px;
      &:before {
        background-image: url("@/assets/img/checked/all-checked-green.svg");
        background-repeat: no-repeat;
        background-size: contain;
        content: "";
        height: 20px;
        width: 20px;
      }
    }
  }
  &__table-wrapper {
    overflow-x: auto;
  }
  &__table-detailing {
    table-layout: fixed;
    min-width: 700px;
    padding-block: 10px;
    overflow-x: auto;
    th, td {
      padding-inline: 20px;
    }
    th:nth-of-type(2) {
      width: 360px;
    }
    td {
      font-size: 14px;
      &:first-of-type {
        text-align: center;
      }
    }
  }
}

@media screen and (max-width: 820px) {
  .patient-visits {
    &__visit {
      &__row {
        grid-template-columns: 1fr;
        grid-gap: 20px;
        align-items: flex-start;
      }
    }
  }
}

@media screen and (max-width: 720px) {
  .patient-visits {
    &__visit {
      &__bite {
        flex-direction: column;
        grid-gap: 20px;
        align-items: flex-start;
        &__container {
          margin-left: 0;
        }
      }
    }
  }
}

@media screen and (max-width: 700px) {
  .patient-profile {
    max-width: 100%;
  }
}

@media screen and (max-width: 620px) {
  .patient-visits {
    &__visit {
      &__group {
        grid-template-columns: 1fr;
        grid-gap: 10px;
      }
    }
  }
  .chief-page {
    &__modal {
      &__buttons {
        grid-template-columns: repeat(2, 150px);
        row-gap: 10px;
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .patient-visits {
    &__diary__buttons__button-print {
      display: none;
    }
    &__visit {
      padding: 20px 15px;
      &__button-save.print {
        display: none;
      }
    }
    &__services {
      &__row {
        flex-direction: column;
        grid-gap: 20px;
        align-items: flex-start;
        margin-bottom: 40px;
      }
    }
  }
  .chief-page {
    &__modal {
      &__buttons {
        grid-template-columns: 1fr;
      }
    }
  }
}
</style>
